import { Business, Groups2, Person } from '@mui/icons-material';
import { Avatar, Box, Chip, Paper, Typography } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { generateFullName } from '@sharedInterfaces/globalHelper';
import type { IOrgUnitDetailDTO } from '@sharedInterfaces/IOrgUnit';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface ProjectCardProps {
    entity: IOrgUnitDetailDTO;
    isSelected?: boolean;
    onSelect?: (id: number) => void;
    showCheckbox?: boolean;
}

/**
 * OrgUnitCard component with modern design.
 *
 * @param {ProjectCardProps} props - The props for the OrgUnitCard component.
 * @returns {JSX.Element} - The rendered OrgUnitCard component.
 */
function OrgUnitCard(props: ProjectCardProps) {
    const { entity, isSelected = false, onSelect, showCheckbox = false } = props;
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).orgUnit;

    const handleClick = () => {
        navigate(`/${ELinks.PEOPLE_ORGUNITS}/${entity.id}`);
    };

    return (
        <Paper
            sx={{
                p: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mb: 2,

                overflow: 'hidden',
                border: isSelected ? 'none' : '1px solid',
                borderColor: 'divider',
                bgcolor: isSelected ? (theme) => theme.palette.primary.main + '1A' : 'background.paper',
            }}
            onClick={handleClick}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'primary.contrastText',
                    p: 2,
                    m: -2,
                    mb: 1,
                    borderRadius: '12px 12px 0 0',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                    <Business sx={{ fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'inherit' }}>
                        {entity.title}
                    </Typography>
                </Box>
                <Chip
                    label={`${entity.employees?.length || 0} MA`}
                    size="small"
                    variant="filled"
                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}
                />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {langStrings.leader}:
                </Typography>
                <Typography variant="body2">
                    {generateFullName(entity.leader.firstname, entity.leader.lastname)}
                </Typography>
            </Box>

            {entity.employees && entity.employees.length > 0 && (
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Groups2 sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {langStrings.employees} ({entity.employees.length}):
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 60, overflow: 'hidden' }}>
                        {entity.employees.slice(0, 6).map((emp) => (
                            <Chip
                                key={emp.id}
                                label={generateFullName(emp.firstname, emp.lastname)}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                            />
                        ))}
                        {entity.employees.length > 6 && (
                            <Chip
                                label={`+${entity.employees.length - 6}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                            />
                        )}
                    </Box>
                </Box>
            )}
        </Paper>
    );
}

export default OrgUnitCard;
